import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, User2, Home, Landmark, Phone, AlertCircle, CheckCircle2, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ROLE = { admin: "Admin", chef: "Chef d'atelier", student: "Étudiant" };

// Champs de profil étendu : ordre + libellés
const PROFILE_FIELDS = [
  "first_name", "last_name", "phone",
  "date_of_birth", "national_register",
  "address_street", "address_postal_code", "address_city", "address_country",
  "iban",
  "emergency_contact_name", "emergency_contact_phone", "emergency_contact_relation",
];

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      setForm(pickFields(r.data));
    });
  }, []);

  const missing = useMemo(() => PROFILE_FIELDS.filter((k) => !me?.[k] && !form?.[k]), [me, form]);
  const complete = missing.length === 0;

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Nettoie les strings vides
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data } = await api.patch("/auth/me", payload);
      setMe(data);
      setForm(pickFields(data));
      toast.success("Profil mis à jour");
      if (typeof refresh === "function") await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  const initial = (user.name || "?").charAt(0).toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="page-profile">
      <div>
        <p className="label-tiny">Profil</p>
        <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">Mon compte</h1>
        <p className="text-[#6b7f79] mt-1">Complète tes informations personnelles pour ton dossier atelier.</p>
      </div>

      {/* Bannière de complétude — visible tant que le profil est incomplet */}
      {!complete && (
        <Card className="card-soft border-0 bg-[#FFF4E5] border border-[#FFB300]" data-testid="profile-incomplete-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#8B5A00] shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-[#1A2B27]">Ton profil est incomplet</div>
              <div className="text-[#6b7f79] mt-0.5">
                Il te reste <b>{missing.length}</b> champ{missing.length > 1 ? "s" : ""} à renseigner pour un dossier complet.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {complete && (
        <Card className="card-soft border-0 bg-[#E8F5E9] border border-[#66BB6A]" data-testid="profile-complete-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#2E7D32] shrink-0 mt-0.5" />
            <div className="text-sm text-[#1A2B27] font-semibold">Ton profil est complet — merci !</div>
          </CardContent>
        </Card>
      )}

      <Card className="card-soft border-0">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2C4C3B] to-[#3C6750] text-white font-display text-2xl flex items-center justify-center shadow shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl text-[#1A2B27] truncate">{user.name}</div>
              <div className="text-sm text-[#6b7f79] flex items-center gap-2 flex-wrap">
                <span className="truncate">{user.email}</span>
                <Badge variant="outline" className="border-[#DDE7E4] text-[#6b7f79] rounded-full text-[10px]">
                  {ROLE[user.role]}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const resp = await api.get(`/users/${user.id}/profile-pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `fiche-${(user.name || "profil").replace(/\s+/g, "_")}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                  toast.success("Fiche PDF téléchargée");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="profile-download-pdf"
              className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E4EBE1]"
            >
              <FileText className="w-4 h-4 mr-2" /> Fiche PDF
            </Button>
            <Button
              variant="outline"
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="profile-logout"
              className="rounded-full border-[#3C6750] text-[#3C6750] hover:bg-[#FFEBEE]"
            >
              <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
            </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section : Identité */}
      <Section icon={<User2 className="w-5 h-5 text-[#2C4C3B]" />} title="Identité">
        <Grid2>
          <Field label="Prénom" testId="profile-first-name">
            <Input value={form.first_name || ""} onChange={on("first_name")} data-testid="profile-first-name-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Nom" testId="profile-last-name">
            <Input value={form.last_name || ""} onChange={on("last_name")} data-testid="profile-last-name-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Téléphone" testId="profile-phone">
            <Input type="tel" value={form.phone || ""} onChange={on("phone")} placeholder="+32 470 12 34 56" data-testid="profile-phone-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Date de naissance" testId="profile-dob">
            <Input type="date" value={form.date_of_birth || ""} onChange={on("date_of_birth")} data-testid="profile-dob-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="N° registre national" testId="profile-nrn" hint="11 chiffres">
            <Input value={form.national_register || ""} onChange={on("national_register")} placeholder="00.00.00-000.00" data-testid="profile-nrn-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
        </Grid2>
      </Section>

      {/* Section : Adresse */}
      <Section icon={<Home className="w-5 h-5 text-[#2C4C3B]" />} title="Adresse">
        <div className="grid grid-cols-1 gap-3">
          <Field label="Rue et numéro" testId="profile-street">
            <Input value={form.address_street || ""} onChange={on("address_street")} placeholder="Rue de l'Atelier 42" data-testid="profile-street-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Code postal" testId="profile-cp">
            <Input value={form.address_postal_code || ""} onChange={on("address_postal_code")} placeholder="1000" data-testid="profile-cp-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Ville" testId="profile-city">
            <Input value={form.address_city || ""} onChange={on("address_city")} placeholder="Bruxelles" data-testid="profile-city-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Pays" testId="profile-country">
            <Input value={form.address_country || ""} onChange={on("address_country")} placeholder="Belgique" data-testid="profile-country-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
        </div>
      </Section>

      {/* Section : Banque */}
      <Section icon={<Landmark className="w-5 h-5 text-[#2C4C3B]" />} title="Coordonnées bancaires">
        <Field label="IBAN" testId="profile-iban">
          <Input value={form.iban || ""} onChange={on("iban")} placeholder="BE68 5390 0754 7034" data-testid="profile-iban-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4] font-mono" />
        </Field>
      </Section>

      {/* Section : Contact d'urgence */}
      <Section icon={<Phone className="w-5 h-5 text-[#2C4C3B]" />} title="Contact d'urgence">
        <Grid2>
          <Field label="Nom" testId="profile-ec-name">
            <Input value={form.emergency_contact_name || ""} onChange={on("emergency_contact_name")} data-testid="profile-ec-name-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Téléphone" testId="profile-ec-phone">
            <Input type="tel" value={form.emergency_contact_phone || ""} onChange={on("emergency_contact_phone")} placeholder="+32 470 12 34 56" data-testid="profile-ec-phone-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
          <Field label="Lien de parenté" testId="profile-ec-relation">
            <Input value={form.emergency_contact_relation || ""} onChange={on("emergency_contact_relation")} placeholder="Parent, conjoint, ami…" data-testid="profile-ec-relation-input" className="mt-1 h-11 rounded-xl border-[#DDE7E4]" />
          </Field>
        </Grid2>
      </Section>

      <div className="flex justify-end sticky bottom-4">
        <Button
          onClick={submit}
          disabled={saving}
          data-testid="profile-save"
          className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] shadow-md h-12 px-6"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer mon profil"}
        </Button>
      </div>
    </div>
  );
}

function pickFields(u) {
  const out = {};
  PROFILE_FIELDS.forEach((k) => { out[k] = u?.[k] || ""; });
  return out;
}

function Section({ icon, title, children }) {
  return (
    <Card className="card-soft border-0">
      <CardContent className="p-6 space-y-3">
        <h2 className="font-display text-lg text-[#1A2B27] flex items-center gap-2">
          {icon} {title}
        </h2>
        {children}
      </CardContent>
    </Card>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children, hint, testId }) {
  return (
    <div data-testid={testId ? `${testId}-field` : undefined}>
      <Label className="label-tiny">{label}{hint ? <span className="opacity-60"> — {hint}</span> : null}</Label>
      {children}
    </div>
  );
}
